import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import axios from "axios";
import {
    ArrowLeft,
    KeyRound,
    Eye,
    EyeOff,
    AlertCircle,
    CheckCircle2,
    Loader2,
    Smartphone,
} from "lucide-react";
import TopNav from "../components/landing/TopNav";
import Footer from "../components/landing/Footer";

const REVEAL = {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.7, ease: [0.2, 0.8, 0.2, 1] },
};

const AUTH_API_URL =
    process.env.REACT_APP_AUTH_API_URL ||
    import.meta?.env?.REACT_APP_AUTH_API_URL ||
    "https://api.getbetween.us";

function useQueryToken() {
    const { search } = useLocation();
    return useMemo(() => {
        const params = new URLSearchParams(search);
        const t = params.get("token");
        return t && t.trim().length > 0 ? t.trim() : "";
    }, [search]);
}

export default function ResetPassword() {
    const reduce = useReducedMotion();
    const { pathname } = useLocation();
    const token = useQueryToken();

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [touched, setTouched] = useState({ password: false, confirm: false });
    const [showPw, setShowPw] = useState(false);
    const [status, setStatus] = useState("idle"); // idle|sending|sent|error
    const [errorMsg, setErrorMsg] = useState("");
    const [userEmail, setUserEmail] = useState("");

    useEffect(() => {
        document.title = "Reset password — BetweenUs";
        window.scrollTo(0, 0);
    }, [pathname]);

    const errors = {
        password:
            password.length < 6
                ? "Use at least 6 characters."
                : "",
        confirm:
            confirm.length === 0
                ? "Confirm your new password."
                : confirm !== password
                  ? "Passwords don’t match."
                  : "",
    };
    const isValid = !errors.password && !errors.confirm;
    const anim = reduce ? {} : REVEAL;

    const onSubmit = async (e) => {
        e.preventDefault();
        setTouched({ password: true, confirm: true });
        if (!isValid || status === "sending" || !token) return;
        setStatus("sending");
        setErrorMsg("");
        try {
            const res = await axios.post(
                `${AUTH_API_URL}/api/auth/reset-password`,
                { token, password },
                { timeout: 15000 },
            );
            const email = res?.data?.user?.email || "";
            setUserEmail(email);
            setStatus("sent");
        } catch (err) {
            const detail =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "";
            const isNetwork = !err?.response;
            const friendly = isNetwork
                ? "We couldn’t reach the server. Check your connection and try again."
                : detail ||
                  "Something went wrong. Please try again.";
            setErrorMsg(friendly);
            setStatus("error");
        }
    };

    const isMobileUA =
        typeof navigator !== "undefined" &&
        /Android|iPhone|iPad/i.test(navigator.userAgent || "");

    const deepLink = token
        ? `betweenus://reset-password?token=${encodeURIComponent(token)}`
        : "";

    return (
        <div
            className="relative min-h-screen bg-paper text-ink flex flex-col"
            data-testid="reset-password-page"
        >
            <TopNav variant="legal" />

            <main className="flex-1">
                <section
                    data-testid="reset-password-hero"
                    className="relative overflow-hidden border-b border-brand"
                >
                    <div className="absolute inset-0 hero-wash pointer-events-none" />
                    <div className="noise-overlay" />
                    <div className="relative mx-auto w-full max-w-2xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-10">
                        <Link
                            to="/"
                            data-testid="reset-back-home"
                            className="inline-flex items-center gap-2 text-[12.5px] font-medium tracking-[-0.01em] text-[var(--brand-muted-2)] hover:text-[var(--brand-ink)] transition-colors"
                        >
                            <ArrowLeft size={14} strokeWidth={2} />
                            Back to home
                        </Link>

                        <motion.div
                            {...anim}
                            className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand bg-white/80 backdrop-blur px-3 py-1 text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--brand-teal-2)]"
                            data-testid="reset-eyebrow"
                        >
                            <KeyRound size={12} strokeWidth={2.2} />
                            Account · Password reset
                        </motion.div>

                        <motion.h1
                            {...anim}
                            transition={{
                                ...(anim.transition || {}),
                                delay: 0.05,
                            }}
                            className="mt-4 font-serif text-[34px] sm:text-[42px] leading-[1.05] tracking-[-0.02em] text-[var(--brand-ink)]"
                            data-testid="reset-title"
                        >
                            Set a new password.
                        </motion.h1>
                        <motion.p
                            {...anim}
                            transition={{
                                ...(anim.transition || {}),
                                delay: 0.1,
                            }}
                            className="mt-3 text-[15.5px] leading-[1.6] text-[var(--brand-muted-2)] max-w-xl"
                            data-testid="reset-subtitle"
                        >
                            Choose a password you’ll remember. We’ll sign you
                            back in once you save.
                        </motion.p>
                    </div>
                </section>

                <section className="relative">
                    <div className="mx-auto w-full max-w-2xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
                        {/* No / invalid token state */}
                        {!token && (
                            <div
                                data-testid="reset-no-token"
                                className="rounded-[18px] bg-white border border-brand shadow-[var(--shadow-soft)] p-6 sm:p-8"
                            >
                                <div className="flex items-start gap-3">
                                    <span
                                        className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(217,119,6,0.10)] text-[#B45309]"
                                        aria-hidden="true"
                                    >
                                        <AlertCircle
                                            size={18}
                                            strokeWidth={2}
                                        />
                                    </span>
                                    <div>
                                        <h2 className="font-serif text-[22px] tracking-[-0.01em] text-[var(--brand-ink)]">
                                            This link is invalid or expired.
                                        </h2>
                                        <p className="mt-2 text-[14.5px] leading-[1.6] text-[var(--brand-muted-2)]">
                                            Reset links expire for your safety.
                                            Request a fresh one and we’ll email
                                            you a new link.
                                        </p>
                                        <div className="mt-5 flex flex-wrap gap-3">
                                            <Link
                                                to="/forgot-password"
                                                data-testid="reset-request-new"
                                                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[var(--brand-navy)] text-[var(--brand-paper)] text-[13.5px] font-semibold tracking-[-0.01em] hover:bg-[var(--brand-navy-2)] hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.98] transition-[background-color,transform,box-shadow] duration-200 shadow-[0_10px_24px_rgba(14,27,42,0.14)] ring-focus"
                                            >
                                                Request a new link
                                            </Link>
                                            <Link
                                                to="/support"
                                                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-brand text-[13.5px] font-semibold tracking-[-0.01em] text-[var(--brand-ink)] hover:bg-[var(--brand-paper-2)] transition-colors"
                                            >
                                                Contact support
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Success state */}
                        {token && status === "sent" && (
                            <div
                                data-testid="reset-success"
                                className="rounded-[18px] bg-white border border-brand shadow-[var(--shadow-soft)] p-6 sm:p-8"
                            >
                                <div className="flex items-start gap-3">
                                    <span
                                        className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(48,140,138,0.12)] text-[var(--brand-teal-2)]"
                                        aria-hidden="true"
                                    >
                                        <CheckCircle2
                                            size={18}
                                            strokeWidth={2}
                                        />
                                    </span>
                                    <div>
                                        <h2 className="font-serif text-[22px] tracking-[-0.01em] text-[var(--brand-ink)]">
                                            Your password has been updated.
                                        </h2>
                                        <p className="mt-2 text-[14.5px] leading-[1.6] text-[var(--brand-muted-2)]">
                                            {userEmail
                                                ? `You’re all set, ${userEmail}. Open the BetweenUs app to sign in with your new password.`
                                                : "You’re all set. Open the BetweenUs app to sign in with your new password."}
                                        </p>
                                        <div className="mt-5 flex flex-wrap gap-3">
                                            <Link
                                                to="/"
                                                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[var(--brand-navy)] text-[var(--brand-paper)] text-[13.5px] font-semibold tracking-[-0.01em] hover:bg-[var(--brand-navy-2)] transition-colors shadow-[0_10px_24px_rgba(14,27,42,0.14)] ring-focus"
                                            >
                                                Back to home
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Form state */}
                        {token && status !== "sent" && (
                            <form
                                noValidate
                                onSubmit={onSubmit}
                                data-testid="reset-form"
                                className="rounded-[18px] bg-white border border-brand shadow-[var(--shadow-soft)] p-6 sm:p-8 space-y-5"
                            >
                                <FieldPassword
                                    id="reset-new-password"
                                    label="New password"
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={(v) => setPassword(v)}
                                    onBlur={() =>
                                        setTouched((t) => ({
                                            ...t,
                                            password: true,
                                        }))
                                    }
                                    show={showPw}
                                    onToggleShow={() => setShowPw((v) => !v)}
                                    error={
                                        touched.password
                                            ? errors.password
                                            : ""
                                    }
                                    testId="reset-input-password"
                                    helper="At least 6 characters."
                                />

                                <FieldPassword
                                    id="reset-confirm-password"
                                    label="Confirm password"
                                    autoComplete="new-password"
                                    value={confirm}
                                    onChange={(v) => setConfirm(v)}
                                    onBlur={() =>
                                        setTouched((t) => ({
                                            ...t,
                                            confirm: true,
                                        }))
                                    }
                                    show={showPw}
                                    onToggleShow={() => setShowPw((v) => !v)}
                                    error={
                                        touched.confirm ? errors.confirm : ""
                                    }
                                    testId="reset-input-confirm"
                                />

                                {status === "error" && errorMsg && (
                                    <div
                                        role="alert"
                                        data-testid="reset-error"
                                        className="flex items-start gap-2.5 rounded-[12px] border border-[rgba(220,38,38,0.25)] bg-[rgba(220,38,38,0.06)] px-3.5 py-3 text-[13.5px] leading-[1.55] text-[#991B1B]"
                                    >
                                        <AlertCircle
                                            size={16}
                                            strokeWidth={2}
                                            className="mt-0.5 shrink-0"
                                        />
                                        <span>{errorMsg}</span>
                                    </div>
                                )}

                                <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-3">
                                    <button
                                        type="submit"
                                        disabled={
                                            status === "sending" || !isValid
                                        }
                                        data-testid="reset-submit"
                                        className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[var(--brand-navy)] text-[var(--brand-paper)] text-[14px] font-semibold tracking-[-0.01em] shadow-[0_10px_24px_rgba(14,27,42,0.14)] hover:bg-[var(--brand-navy-2)] hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.98] transition-[background-color,transform,box-shadow] duration-200 ring-focus disabled:opacity-60 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
                                    >
                                        {status === "sending" ? (
                                            <>
                                                <Loader2
                                                    size={16}
                                                    className="animate-spin"
                                                />
                                                Saving…
                                            </>
                                        ) : (
                                            <>Save new password</>
                                        )}
                                    </button>

                                    <Link
                                        to="/"
                                        data-testid="reset-cancel"
                                        className="inline-flex items-center justify-center h-12 px-5 text-[13.5px] font-medium tracking-[-0.01em] text-[var(--brand-muted-2)] hover:text-[var(--brand-ink)] transition-colors"
                                    >
                                        Back to sign in
                                    </Link>
                                </div>

                                {isMobileUA && deepLink && (
                                    <div className="pt-2 mt-2 border-t border-brand">
                                        <a
                                            href={deepLink}
                                            data-testid="reset-open-in-app"
                                            className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-brand text-[12.5px] font-semibold tracking-[-0.01em] text-[var(--brand-ink)] hover:bg-[var(--brand-paper-2)] transition-colors"
                                        >
                                            <Smartphone
                                                size={14}
                                                strokeWidth={2}
                                            />
                                            Continue in the BetweenUs app
                                        </a>
                                        <p className="mt-2 text-[12px] text-[var(--brand-muted)]">
                                            Have the app installed? This opens
                                            it directly.
                                        </p>
                                    </div>
                                )}
                            </form>
                        )}

                        <p className="mt-6 text-[12.5px] text-[var(--brand-muted)]">
                            Trouble resetting? Email{" "}
                            <a
                                href="mailto:support@tylegn.com"
                                className="underline decoration-[var(--brand-teal-2)] underline-offset-2 hover:text-[var(--brand-ink)]"
                            >
                                support@tylegn.com
                            </a>
                            .
                        </p>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}

function FieldPassword({
    id,
    label,
    value,
    onChange,
    onBlur,
    error,
    helper,
    autoComplete = "new-password",
    show = false,
    onToggleShow,
    testId,
}) {
    const invalid = Boolean(error);
    return (
        <div>
            <label
                htmlFor={id}
                className="block text-[12.5px] font-semibold tracking-[0.02em] text-[var(--brand-ink)]"
            >
                {label}
            </label>
            <div className="relative mt-1.5">
                <input
                    id={id}
                    name={id}
                    type={show ? "text" : "password"}
                    autoComplete={autoComplete}
                    minLength={6}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    aria-invalid={invalid || undefined}
                    aria-describedby={
                        invalid ? `${id}-error` : helper ? `${id}-help` : undefined
                    }
                    data-testid={testId}
                    className={`w-full h-11 rounded-[12px] border bg-white px-3.5 pr-11 text-[14.5px] text-[var(--brand-ink)] placeholder:text-[var(--brand-muted)] outline-none transition-colors ring-focus ${
                        invalid
                            ? "border-[rgba(220,38,38,0.45)]"
                            : "border-brand focus:border-[var(--brand-teal-2)]"
                    }`}
                />
                <button
                    type="button"
                    onClick={onToggleShow}
                    aria-label={show ? "Hide password" : "Show password"}
                    data-testid={`${testId}-toggle`}
                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--brand-muted-2)] hover:text-[var(--brand-ink)] hover:bg-[var(--brand-paper-2)] transition-colors"
                >
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
            </div>
            {invalid ? (
                <p
                    id={`${id}-error`}
                    className="mt-1.5 text-[12.5px] text-[#991B1B]"
                >
                    {error}
                </p>
            ) : helper ? (
                <p
                    id={`${id}-help`}
                    className="mt-1.5 text-[12px] text-[var(--brand-muted)]"
                >
                    {helper}
                </p>
            ) : null}
        </div>
    );
}
